import "Mention/Scripts/TFS.Mention.WorkItems.Registration"; // to register work-item mention parser and provider

import * as Controls from "VSS/Controls";
import * as Utils_String from "VSS/Utils/String";

import { MentionProcessor } from "Mention/Scripts/TFS.Mention";
import { IAutocompletePluginOptions, MentionType } from "Mention/Scripts/TFS.Mention.Autocomplete";
import { AutocompleteEnhancement, IAutocompleteOptions, IAutocompletePluginConfig } from "Mention/Scripts/TFS.Mention.Autocomplete.Controls";
import { PersonAutocompleteProvider } from "Mention/Scripts/TFS.Mention.People";
import { WorkItemAutocompleteProvider } from "Mention/Scripts/TFS.Mention.WorkItems";
import { WikiPage, WikiType } from "TFS/Wiki/Contracts";
import { getDefaultTelemetryPropsForMentionFeatures, WikiTypeCI } from "Wiki/Scripts/CustomerIntelligenceConstants";
import { WikiIncludeAutoCompleteProvider } from "Wiki/Scripts/WikiIncludeAutoCompleteProvider";
import { WikiLinkAutocompleteProvider } from "Wiki/Scripts/WikiLinkAutoCompleteProvider";

interface Point {
    x: number;
    y: number;
}

export class WikiMentionNwpWrapper {
    private _mentionEnhancement: Controls.Enhancement<IAutocompleteOptions>;
    private _textControl: HTMLTextAreaElement;

    public constructor(
        textArea: HTMLTextAreaElement,
        getPagesToFilter: () => IPromise<WikiPage[]>,
        getPagesToFilterExcludingCurrent: () => IPromise<WikiPage[]>,
        onSelect: () => void,
        wikiType: WikiType,

       // Following refs are required for auto complete.
       private _textEditorMirrorContainerRef: HTMLDivElement,
       private _textEditorMirrorRef: HTMLSpanElement,
       private _positioningElementRef: HTMLDivElement,
       
       private _isIncludeEditingAllowed: boolean
    ) {
        this._textControl = textArea;
        const autoCompleteOptions = this._getAutoCompleteOptions(getPagesToFilter, getPagesToFilterExcludingCurrent, onSelect, wikiType);

        this._mentionEnhancement = Controls.Enhancement.enhance(AutocompleteEnhancement, $(textArea), autoCompleteOptions);
    }

    public dispose(): void {
        this._mentionEnhancement.dispose();
        this._mentionEnhancement = null;
    }

    private _getAutoCompleteOptions(
        getPagesToFilter: () => IPromise<WikiPage[]>,
        getPagesToFilterExcludingCurrent: () => IPromise<WikiPage[]>,
        onSelect: () => void,
        wikiType: WikiType
    ): IAutocompleteOptions {
        const pluginConfigs: IAutocompletePluginConfig<IAutocompletePluginOptions>[] = [];

        pluginConfigs.push({
            factory: (options: IAutocompletePluginOptions) => {
                options.positioningElement = this._getPositioningWIElement;
                return new WorkItemAutocompleteProvider(options);
            }
        });

        pluginConfigs.push({
            factory: (options: IAutocompletePluginOptions) => {
                options.positioningElement = this._getPositioningLinkElement;
                return new WikiLinkAutocompleteProvider(options, getPagesToFilter);
            }
        });

        pluginConfigs.push({
            factory: (options: IAutocompletePluginOptions) => {
                options.positioningElement = this._getPositioningPeopleElement;
                return new PersonAutocompleteProvider(options);
            }
        });

        if (this._isIncludeEditingAllowed) {
            pluginConfigs.push({
                factory: (options: IAutocompletePluginOptions) => {
                    options.positioningElement = this._getPositioningElementForIncludePages;
                    return new WikiIncludeAutoCompleteProvider(options, getPagesToFilterExcludingCurrent);
                }
            });
        }

        const wikiTypeCi = wikiType === WikiType.CodeWiki ? WikiTypeCI.CodeWiki : WikiTypeCI.ProjectWiki;
        let telemetryProps = getDefaultTelemetryPropsForMentionFeatures(wikiTypeCi);
        telemetryProps["newWiki"] = true;

        return {
            mentionType: MentionType.WorkItem,
            pluginConfigs: pluginConfigs,
            select: onSelect,
            defaultTelemetryProperties: telemetryProps
        };
    }

    private _getPositioningWIElement = (): JQuery => {
        return this._getPositioningElementForLastIndexOf("#");
    };

    private _getPositioningPeopleElement = (): JQuery => {
        return this._getPositioningElementForLastIndexOf("@");
    };

    private _getPositioningLinkElement = (): JQuery => {
        // Look for link autocomplete
        const linkMentionRegex: RegExp = WikiLinkAutocompleteProvider.wikiStricktlyOpenLinkPattern();
        let linkMatch: RegExpExecArray = null;
        let linkAutoCompleteMatchLocation = -1;
        do {
            linkMatch = linkMentionRegex.exec(this._textControl.value.substring(0, this._textControl.selectionStart));

            if (linkMatch) {
                linkAutoCompleteMatchLocation = linkMatch.index;
            }
        } while (linkMatch !== null);

        if (linkAutoCompleteMatchLocation > 0) {
            const replacementString: string = this._getReplacementString(this._textControl.value, 0, linkAutoCompleteMatchLocation);
            this._textEditorMirrorRef.innerHTML = Utils_String.htmlEncode(replacementString);
        }

        return this._getPositioningElement();
    };

    private _getPositioningElementForIncludePages = (): JQuery => {
        const textTillCursor = this._textControl.value.substr(0, this._textControl.selectionStart);
        const substringLength = WikiIncludeAutoCompleteProvider.getStartIndexOfIncludePath(textTillCursor);
        const replacementString: string = this._getReplacementString(this._textControl.value, 0, substringLength);
        this._textEditorMirrorRef.innerHTML = Utils_String.htmlEncode(replacementString);

        return this._getPositioningElement();
    };

    private _getPositioningElementForLastIndexOf = (searchString: string): JQuery => {
        const substringLength = this._textControl.value.lastIndexOf(searchString, this._textControl.selectionStart) + 1;
        const replacementString: string = this._getReplacementString(this._textControl.value, 0, substringLength);
        this._textEditorMirrorRef.innerHTML = Utils_String.htmlEncode(replacementString);

        return this._getPositioningElement();
    };

    private _getPositioningElement = (): JQuery => {
        const lineHeight = 20;
        const suggestionDropDownWidth = 600;

        /**
         * If there is a scroll appearing in original text area the width where the text could be rendered is reduced,
         * this should be reflected in mirror span as well to exactly overlap the original text area.
         */
        if (this._textControl.scrollHeight > this._textControl.clientHeight) {
            this._textEditorMirrorContainerRef.style.overflowY = "scroll";
        } else {
            this._textEditorMirrorContainerRef.style.overflowY = "hidden";
        }

        /**
         * Rectangles cordinates are wrt to viewport (not parent element) in px. However, we need to
         * position the dropdown wrt ".markdown-editable" div which contains the textArea.
         * For this we assume the first character in textArea approximately starts at top left corner of textArea.
         * Then all that needs to done is to find the offset position in px of last '#' before caret relative
         * to first character. We need to make sure we adjust for scrolling as well.
         */
        const caretOffset: Point = this._getCursorOffsetRespectToFirstCharacter();

        this._positioningElementRef.style.width = `${suggestionDropDownWidth}px`;
        this._positioningElementRef.style.height = `${lineHeight}px`;
        this._positioningElementRef.style.top = `${caretOffset.y}px`;

        /**
         * We need to ensure if the drop-down is overflowing ".edit-preview-container" the drop-down's
         * right is aligned with right of ".edit-preview-container".
         */
        const editPreviewContainerWidth: number = document.getElementsByClassName("we-text-preview-container")[0].clientWidth;
        const isDropDownOverflowingAlongX: boolean = caretOffset.x + suggestionDropDownWidth > editPreviewContainerWidth;
        if (!isDropDownOverflowingAlongX) {
            this._positioningElementRef.style.left = `${caretOffset.x}px`;
            this._positioningElementRef.style.right = `auto`;
        } else {
            this._positioningElementRef.style.right = `${0}px`;
            this._positioningElementRef.style.left = `auto`;
        }

        this._textEditorMirrorRef.innerHTML = "";
        return $(this._positioningElementRef);
    };

    private _getCursorOffsetRespectToFirstCharacter = (): Point => {
        const mirrorSpanRects = this._textEditorMirrorRef.getClientRects();
        const lastRect: ClientRect = mirrorSpanRects[mirrorSpanRects.length - 1];
        const firstRect: ClientRect = mirrorSpanRects[0];

        const topPadding: string = window.getComputedStyle(this._textControl, null).getPropertyValue("padding-top");
        const caretOffsetY: number = lastRect.top - firstRect.top - this._textControl.scrollTop + Number(topPadding.replace("px", ""));
        const caretOffsetX: number = lastRect.left - firstRect.left - this._textControl.scrollLeft + lastRect.width;

        return { x: caretOffsetX, y: caretOffsetY };
    };

    private _getReplacementString = (originalString: string, start: number, end: number): string => {
        return originalString.substring(start, end).replace(/\n$/, "\n\001");
    };
}

export function translateDisplayNamesToStorageKeysOfPersonMentions(content: string): string {
    return MentionProcessor.getDefault().translateDisplayNamesToStorageKeysOfPersonMentions(content);
}

export function translateStorageKeysToDisplayNamesOfPersonMentions(content: string): IPromise<string> {
    return MentionProcessor.getDefault().translateStorageKeysToDisplayNamesOfPersonMentions(content);
}
